import React from 'react';
import { Pressable, StyleProp, Text, View, ViewStyle } from 'react-native';
import { borderRadius, colors, spacing, typography } from '../../tokens';
import { themedStyleSheet } from '../../../../themes/themedStyleSheet';

export interface SegmentedControlOption {
  /**
   * Unique identifier for the option
   */
  value: string;

  /**
   * Label text to display
   */
  label: string;

  /**
   * Whether this option is disabled
   */
  disabled?: boolean;
}

export interface SegmentedControlProps {
  /**
   * Array of options to display
   */
  options: SegmentedControlOption[];

  /**
   * Currently selected option value
   */
  value: string;

  /**
   * Callback when an option is selected
   */
  onChange: (value: string) => void;

  /**
   * Whether the entire control is disabled
   */
  disabled?: boolean;

  /**
   * Test ID for testing
   */
  testID?: string;

  /**
   * Optional style overrides for the container
   */
  style?: StyleProp<ViewStyle>;
}

export const SegmentedControl: React.FC<SegmentedControlProps> = ({
  options,
  value,
  onChange,
  disabled = false,
  testID,
  style,
}) => {
  return (
    <View
      style={[styles.container, style]}
      testID={testID}
      accessibilityRole="tablist"
    >
      <View style={styles.buttonsContainer}>
        {options.map((option, index) => {
          const isSelected = option.value === value;
          const isDisabled = disabled || option.disabled;

          return (
            <Pressable
              key={option.value}
              onPress={() => !isDisabled && onChange(option.value)}
              disabled={isDisabled}
              style={[
                styles.option,
                index === 0 && styles.firstOption,
                index === options.length - 1 && styles.lastOption,
                isSelected && styles.selectedOption,
                isDisabled && styles.disabledOption,
              ]}
              accessibilityRole="tab"
              accessibilityState={{
                selected: isSelected,
                disabled: isDisabled,
              }}
              testID={`${testID}-option-${option.value}`}
            >
              {isSelected && <View style={styles.selectedBackground} />}
              <Text
                numberOfLines={1}
                style={[
                  styles.label,
                  isSelected && styles.selectedLabel,
                  isDisabled && styles.disabledLabel,
                ]}
              >
                {option.label}
              </Text>
            </Pressable>
          );
        })}
      </View>
    </View>
  );
};

SegmentedControl.displayName = 'SegmentedControl';

const styles = themedStyleSheet((role) => ({
  container: {
    backgroundColor: role.surfaceHover,
    borderRadius: borderRadius.full, // 9999px for full pill shape
    padding: spacing.xs,
    alignSelf: 'flex-start',
  },
  buttonsContainer: {
    flexDirection: 'row',
    gap: 0,
  },
  option: {
    flex: 1,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    minHeight: 32,
  },
  firstOption: {
    borderTopLeftRadius: borderRadius.full,
    borderBottomLeftRadius: borderRadius.full,
  },
  lastOption: {
    borderTopRightRadius: borderRadius.full,
    borderBottomRightRadius: borderRadius.full,
  },
  selectedOption: {
    // No additional styles needed, background handled by selectedBackground
  },
  selectedBackground: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: role.surface,
    borderRadius: borderRadius.pill,
    shadowColor: colors.neutral[800],
    shadowOffset: {
      width: 0,
      height: 1,
    },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  disabledOption: {
    opacity: 0.5,
  },
  label: {
    ...typography.body.small,
    color: role.textStrong,
    zIndex: 1,
  },
  selectedLabel: {
    color: role.textStrong,
    fontWeight: '400',
  },
  disabledLabel: {
    opacity: 0.5,
  },
}));
